import { MetricCard, PlanDeviationBadge } from './ui/MetricCard'
import { MetricsSummary } from '@/types'
import { PlanData } from '@/hooks/useStudioAnalytics'
import { WeekMetrics } from '@/hooks/useAnalyticsMetrics'
import { MetricWeek } from '@/hooks/useMetricWeeks'
import { MetricPeriod } from '@/lib/utils'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { calculateWeeklyAverage } from '@/lib/analytics'
import { Coins, Target } from 'lucide-react'

export interface WeeklyPlanData {
  weekly_gross_revenue_plan?: number
  weekly_new_sales_amount_plan?: number
  weekly_renewals_amount_plan?: number
  weekly_returning_clients_amount_plan?: number
  weekly_upgrades_amount_plan?: number
  weekly_leads_plan?: number
  weekly_appointments_plan?: number
  weekly_new_visitors_plan?: number
  weekly_new_sales_plan?: number
}

export interface MainMetricsCardsProps {
  viewMode: 'period' | 'current_week'
  periodMetrics: MetricsSummary
  weekMetrics?: WeekMetrics | null
  planData?: PlanData | null
  weeklyPlanData?: WeeklyPlanData | null
  currentWeek?: MetricWeek | null
  currentPeriod?: MetricPeriod | null
  periodDaysCount: number
  crmComparison?: {
    appRevenue: number
    crmRevenue: number | null
  }
  onOpenCrmDetails?: () => void
  currency?: string
}

const DAY_MS = 24 * 60 * 60 * 1000

function parseIsoLocalDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

function getElapsedDaysToDate(start: string, end: string, targetDate: Date): number {
  const startDate = parseIsoLocalDate(start)
  const endDate = parseIsoLocalDate(end)

  if (targetDate < startDate) return 0

  const effectiveEnd = targetDate < endDate ? targetDate : endDate
  const diff = effectiveEnd.getTime() - startDate.getTime()
  if (diff < 0) return 0
  return Math.floor(diff / DAY_MS) + 1
}

function getElapsedDaysToYesterday(start: string, end: string): number {
  const yesterday = new Date()
  yesterday.setHours(0, 0, 0, 0)
  yesterday.setDate(yesterday.getDate() - 1)
  return getElapsedDaysToDate(start, end, yesterday)
}

function getElapsedDaysToToday(start: string, end: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return getElapsedDaysToDate(start, end, today)
}

function calculateRemainingDaysInTimeframe(start: string, end: string, todayStr: string): number {
  const startDate = parseIsoLocalDate(start)
  const endDate = parseIsoLocalDate(end)
  const todayDate = parseIsoLocalDate(todayStr)

  if (todayDate < startDate) {
    return Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / DAY_MS) + 1)
  }

  if (todayDate > endDate) {
    return 0
  }

  return Math.max(0, Math.floor((endDate.getTime() - todayDate.getTime()) / DAY_MS) + 1)
}

export function MainMetricsCards({
  viewMode,
  periodMetrics,
  weekMetrics,
  planData,
  weeklyPlanData,
  currentWeek,
  currentPeriod,
  periodDaysCount,
  crmComparison,
  onOpenCrmDetails,
  currency = 'RUB'
}: MainMetricsCardsProps) {
  const displayMetrics = viewMode === 'current_week' && weekMetrics
    ? weekMetrics
    : periodMetrics

  const weeklyAvgRevenueCalendar = calculateWeeklyAverage(
    periodMetrics.total_revenue,
    currentPeriod?.period_len_days || periodDaysCount
  )

  const todayStr = getLocalIsoDate()
  const isCurrentSelectedPeriod = !!currentPeriod &&
    currentPeriod.period_start <= todayStr &&
    todayStr <= currentPeriod.period_end

  let weeklyRevenueSubtitle = `ср. недельная: ${formatCurrency(weeklyAvgRevenueCalendar, currency)}`
  const showWeeklyRevenueSubtitle = false

  if (viewMode === 'current_week' && currentWeek && weekMetrics) {
    const filledDays = Math.max(0, weekMetrics.days_with_data || 0)
    const weekLenDays = Math.max(1, currentWeek.week_len_days || weekMetrics.week_len_days || 7)
    const weeklyForecast = filledDays > 0
      ? (weekMetrics.total_revenue / filledDays) * weekLenDays
      : 0

    weeklyRevenueSubtitle = filledDays > 0
      ? `прогноз недели: ${formatCurrency(weeklyForecast, currency)}`
      : 'прогноз недели: нет данных'
  } else if (isCurrentSelectedPeriod && currentWeek && weekMetrics) {
    const completedWeeksCount = Math.max(0, currentWeek.week_number_in_period - 1)
    const completedWeeksRevenue = Math.max(0, periodMetrics.total_revenue - weekMetrics.total_revenue)
    const completedWeeksAvg = completedWeeksCount > 0
      ? completedWeeksRevenue / completedWeeksCount
      : null

    weeklyRevenueSubtitle = completedWeeksAvg !== null
      ? `ср. в неделю: ${formatCurrency(completedWeeksAvg, currency)}`
      : `текущая неделя: ${formatCurrency(weekMetrics.total_revenue || 0, currency)}`
  }

  const weeklyRevenuePlan = weeklyPlanData?.weekly_gross_revenue_plan || 0
  const shouldUseWeeklyPlan = viewMode === 'current_week' && !!weekMetrics && weeklyRevenuePlan > 0
  const revenuePlan = shouldUseWeeklyPlan
    ? weeklyRevenuePlan
    : (planData?.total_gross_revenue_plan || 0)

  let deviationPlan = revenuePlan
  if (revenuePlan > 0) {
    if (shouldUseWeeklyPlan && currentWeek) {
      const weekDays = Math.max(1, currentWeek.week_len_days || weekMetrics?.week_len_days || 7)
      const elapsedDays = getElapsedDaysToYesterday(currentWeek.week_start, currentWeek.week_end)
      deviationPlan = elapsedDays > 0 ? revenuePlan * (elapsedDays / weekDays) : 0
    } else if (currentPeriod) {
      const periodDays = Math.max(1, currentPeriod.period_len_days || periodDaysCount)
      const elapsedDays = getElapsedDaysToYesterday(currentPeriod.period_start, currentPeriod.period_end)
      deviationPlan = elapsedDays > 0 ? revenuePlan * (elapsedDays / periodDays) : 0
    }
  }

  const revenueValue = revenuePlan > 0
    ? `${formatCurrency(displayMetrics.total_revenue, currency)} / ${formatCurrency(revenuePlan, currency)}`
    : formatCurrency(displayMetrics.total_revenue, currency)
  const planExecutionPct = revenuePlan > 0
    ? (displayMetrics.total_revenue / revenuePlan) * 100
    : 0

  let shouldBeYesterdayPct = 0
  let shouldBeTodayPct = 0
  if (shouldUseWeeklyPlan && currentWeek) {
    const weekDays = Math.max(1, currentWeek.week_len_days || weekMetrics?.week_len_days || 7)
    const elapsedYesterday = getElapsedDaysToYesterday(currentWeek.week_start, currentWeek.week_end)
    const elapsedToday = getElapsedDaysToToday(currentWeek.week_start, currentWeek.week_end)
    shouldBeYesterdayPct = (elapsedYesterday / weekDays) * 100
    shouldBeTodayPct = (elapsedToday / weekDays) * 100
  } else if (currentPeriod) {
    const periodDays = Math.max(1, currentPeriod.period_len_days || periodDaysCount)
    const elapsedYesterday = getElapsedDaysToYesterday(currentPeriod.period_start, currentPeriod.period_end)
    const elapsedToday = getElapsedDaysToToday(currentPeriod.period_start, currentPeriod.period_end)
    shouldBeYesterdayPct = (elapsedYesterday / periodDays) * 100
    shouldBeTodayPct = (elapsedToday / periodDays) * 100
  }

  const crmDiff = crmComparison
    ? crmComparison.appRevenue - (crmComparison.crmRevenue ?? 0)
    : null
  const crmDiffPercent = crmComparison && crmComparison.crmRevenue !== null && crmComparison.crmRevenue !== 0
    ? (crmDiff! / crmComparison.crmRevenue) * 100
    : null
  const crmDiffAmount = crmDiff === null
    ? null
    : `${crmDiff >= 0 ? '+' : '-'}${formatCurrency(Math.abs(crmDiff), currency)}`
  const hasCrmContext = crmComparison !== undefined
  const shouldShowCrmBlock = hasCrmContext || Boolean(onOpenCrmDetails)

  const flowDaysBase = viewMode === 'current_week'
    ? Math.max(1, currentWeek?.week_len_days || 7)
    : Math.max(1, currentPeriod?.period_len_days || periodDaysCount)

  const dailyFlowTargets = viewMode === 'current_week'
    ? {
        leads: (weeklyPlanData?.weekly_leads_plan || 0) / flowDaysBase,
        appointments: (weeklyPlanData?.weekly_appointments_plan || 0) / flowDaysBase,
        visitors: (weeklyPlanData?.weekly_new_visitors_plan || 0) / flowDaysBase,
        sales: (weeklyPlanData?.weekly_new_sales_plan || 0) / flowDaysBase
      }
    : {
        leads: (planData?.leads_count_plan || 0) / flowDaysBase,
        appointments: (planData?.appointments_count_plan || 0) / flowDaysBase,
        visitors: (planData?.new_visitors_count_plan || 0) / flowDaysBase,
        sales: (planData?.new_sales_count_plan || 0) / flowDaysBase
      }

  return (
    <>
      <MetricCard
        icon={Coins}
        iconColor="text-success"
        title="Выручка"
        value={revenueValue}
        valueColor="text-success"
        subtitle={
          <div className="space-y-1">
            {showWeeklyRevenueSubtitle && <div>{weeklyRevenueSubtitle}</div>}
            <div className="text-xs">
              Выполнение плана {planExecutionPct.toFixed(1)}%
            </div>
            <div className="text-xs">
              Должно быть на вчера и сегодня {shouldBeYesterdayPct.toFixed(1)}% и {shouldBeTodayPct.toFixed(1)}%
            </div>
            {shouldShowCrmBlock && (
              <div className="mt-3 border-t border-border pt-2">
                <div className="flex items-start justify-between gap-2">
                  {onOpenCrmDetails && (
                    <button
                      type="button"
                      onClick={onOpenCrmDetails}
                      className="text-xs px-2.5 py-1 rounded-md border border-border hover:bg-muted transition-colors"
                    >
                      Сверка с Listok CRM
                    </button>
                  )}
                  {hasCrmContext && (
                    <div className={`text-xs text-right ${crmDiff === null ? 'text-muted-foreground' : crmDiff === 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {crmDiffAmount === null ? (
                        <div>
                          {onOpenCrmDetails
                            ? formatCurrency(0, currency)
                            : `Listok CRM: ${formatCurrency(0, currency)}`}
                        </div>
                      ) : (
                        <div>
                          {onOpenCrmDetails ? '' : 'Listok CRM: '}Отклонение {crmDiffAmount}{crmDiffPercent !== null ? ` (${crmDiffPercent >= 0 ? '+' : ''}${crmDiffPercent.toFixed(2)}%)` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        }
        badge={
          deviationPlan > 0 ? (
            <PlanDeviationBadge
              factValue={displayMetrics.total_revenue}
              planValue={deviationPlan}
            />
          ) : undefined
        }
      />

      <DailyTargetCard
        viewMode={viewMode}
        currentRevenue={displayMetrics.total_revenue}
        planRevenue={revenuePlan}
        dailyFlowTargets={dailyFlowTargets}
        currentWeek={currentWeek}
        currentPeriod={currentPeriod}
        currency={currency}
      />
    </>
  )
}

interface DailyTargetCardProps {
  viewMode: 'period' | 'current_week'
  currentRevenue: number
  planRevenue: number
  dailyFlowTargets?: {
    leads: number
    appointments: number
    visitors: number
    sales: number
  } | null
  currentWeek?: MetricWeek | null
  currentPeriod?: MetricPeriod | null
  currency?: string
}

function DailyTargetCard({
  viewMode,
  currentRevenue,
  planRevenue,
  dailyFlowTargets,
  currentWeek,
  currentPeriod,
  currency = 'RUB'
}: DailyTargetCardProps) {
  const periodLabel = viewMode === 'current_week' ? 'недели' : 'периода'
  const todayStr = getLocalIsoDate()
  const timeframeStart = viewMode === 'current_week' ? currentWeek?.week_start : currentPeriod?.period_start
  const timeframeEnd = viewMode === 'current_week' ? currentWeek?.week_end : currentPeriod?.period_end
  const timeframeDays = viewMode === 'current_week'
    ? Math.max(1, currentWeek?.week_len_days || 7)
    : Math.max(1, currentPeriod?.period_len_days || 1)
  const isCompletedTimeframe = !!timeframeEnd && timeframeEnd < todayStr

  const hasTimeframeData = viewMode === 'current_week' ? !!currentWeek : !!currentPeriod
  if (!hasTimeframeData) {
    return (
      <MetricCard
        icon={Target}
        iconColor="text-muted-foreground"
        title="Цифра дня"
        value="—"
        valueColor="text-muted-foreground"
        subtitle={
          <span className="text-xs text-muted-foreground">
            Нет данных о {periodLabel === 'недели' ? 'неделе' : 'периоде'}
          </span>
        }
      />
    )
  }

  if (!planRevenue || planRevenue === 0) {
    return (
      <MetricCard
        icon={Target}
        iconColor="text-muted-foreground"
        title="Цифра дня"
        value="—"
        valueColor="text-muted-foreground"
        subtitle={
          <span className="text-xs text-muted-foreground">
            План на {periodLabel === 'недели' ? 'неделю' : 'период'} не найден
          </span>
        }
      />
    )
  }

  const remainingRevenue = planRevenue - currentRevenue

  let daysRemaining = 0
  if (isCompletedTimeframe) {
    daysRemaining = 0
  } else if (timeframeStart && timeframeEnd) {
    daysRemaining = calculateRemainingDaysInTimeframe(timeframeStart, timeframeEnd, todayStr)
  }

  const dailyTarget = daysRemaining > 0 ? remainingRevenue / daysRemaining : 0
  const averageDailyRevenue = timeframeDays > 0 ? currentRevenue / timeframeDays : 0
  const cardTitle = isCompletedTimeframe ? 'Средняя фактическая выручка за день' : 'Цифра дня'
  const cardValue = isCompletedTimeframe ? Math.max(0, averageDailyRevenue) : Math.max(0, dailyTarget)

  const formatDaily = (value: number) => {
    if (!Number.isFinite(value)) return '0'
    const rounded = Math.round(value * 10) / 10
    return Number.isInteger(rounded) ? formatNumber(rounded) : rounded.toLocaleString('ru-RU')
  }

  const miniFunnelStages = dailyFlowTargets
    ? [
        { key: 'leads', label: 'Заявки', value: dailyFlowTargets.leads, width: 100, color: '#FFC107' },
        { key: 'appointments', label: 'Записи', value: dailyFlowTargets.appointments, width: 84, color: '#FFD54F' },
        { key: 'visitors', label: 'Приходы', value: dailyFlowTargets.visitors, width: 68, color: '#FFE082' },
        { key: 'sales', label: 'Продажи', value: dailyFlowTargets.sales, width: 52, color: '#FFECB3' }
      ]
    : []

  return (
    <MetricCard
      icon={Target}
      iconColor="text-warning"
      title={cardTitle}
      value={formatCurrency(cardValue, currency)}
      valueColor={remainingRevenue > 0 ? 'text-warning' : 'text-success'}
      subtitle={
        <div className="text-xs">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              <div>
                Осталось до плана {periodLabel}:{' '}
                <span className="font-semibold">{formatCurrency(Math.max(0, remainingRevenue), currency)}</span>
              </div>
              <div className="text-muted-foreground">
                Осталось дней: <span className="font-semibold">{daysRemaining}</span>
              </div>
            </div>
            {miniFunnelStages.length > 0 && (
              <div className="w-[140px] -mt-10 space-y-1 shrink-0">
                {miniFunnelStages.map((stage) => (
                  <div
                    key={stage.key}
                    className="ml-auto h-5 rounded-sm text-[10px] leading-none font-semibold text-amber-900 flex items-center justify-between px-1.5"
                    style={{ width: `${stage.width}%`, backgroundColor: stage.color }}
                  >
                    <span>{stage.label}</span>
                    <span>{formatDaily(stage.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  )
}
